// MACHINERY V2 (Stroje) — catalog browse page (M2.8).
//
// Spec: data package §11 in full. Reference impl:
// design_handoff_machinery_v3/machinery-catalog.jsx (`Catalog`);
// components/CatalogBrowsePage.tsx for the data flow. Artboards 0900
// (refined-claude) / 0901 (machineid-native) — one layout, two palettes.
//
// LAYOUT: rail 248px | main (breadcrumb, title + model search, category tabs,
// the showing-line, a 3-up card grid, compact pagination).
//
// >>> READINESS IS DERIVED ONCE PER MODEL, HERE. <<<
// `deriveReadinessPresentation` is the single entry point (M2.9, locked) and no
// component below re-derives anything. `ctx` is deliberately OMITTED: a real
// gateway connection only exists for a machine INSTANCE, never for a catalog
// model (midasReadiness.ts:83-90), so every catalog presentation comes back
// `connected: false` / `interactive: true` and the badges are clickable.
//
// >>> THE POPUP IS MOUNTED AT PAGE LEVEL, NOT INSIDE A CARD. <<<
// Same rule as FleetPage.tsx:276-290: the popup must not be a React ancestor of
// a navigating card, or a click on its Close / backdrop / CTA re-enters the
// card's onClick. It also carries its own stopPropagation guards on both
// portalled siblings, which is what protects the in-card case.
//
// >>> aiProvenance "unknown": THE DELIBERATE-EYES PASS FOUND A REAL DEFECT. <<<
// The ticket asks for deliberate eyes on the fifth enum member when M2.8
// consumes it. The value is produced in ONE place (readinessPresentation.ts
// `aiProvenanceFrom` — "docs exist but no LIT lane names a source") and read in
// ONE place (CapabilityBadge's `tipCopy`). That reader tested only oem and
// direct and let EVERYTHING else fall into the fanout branch, so an "unknown"
// model's tooltip claimed "via the control-system package" — fabricating the
// provenance the member exists to withhold. Fixed in CapabilityBadge.tsx (see
// the block comment there); "unknown" now yields a null hint and the tooltip
// shows the doc count alone.
// THIS PAGE needed no branch and got none: it renders `summary.docPart`, which
// is provenance-independent. A `default:` here would have been a second opinion
// on a closed enum.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import usePermission from "../../../../shared/hooks/usePermission";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { Modules } from "../../../../core/models/PermissionModels";
import { CatalogModelListItem } from "../../models/models";
import RetryNotice from "../feedback/RetryNotice";
import MachineryToastHost from "../feedback/MachineryToastHost";
import ReadinessPopupV2 from "../readiness/ReadinessPopupV2";
import {
  deriveReadinessPresentation,
  readinessSummaryStrings,
} from "../readiness/readinessPresentation";
import CatalogAddDialogV2 from "./CatalogAddDialogV2";
import CatalogCategoryTabsV2 from "./CatalogCategoryTabsV2";
import CatalogFilterRail from "./CatalogFilterRail";
import CatalogModelCardV2 from "./CatalogModelCardV2";
import CatalogPagination from "./CatalogPagination";
import {
  CATALOG_V2_PATH,
  CatalogBreadcrumb,
  CatalogSearchBox,
  CatalogTitle,
  catalogAddedPath,
} from "./catalogChrome";
import { buildCatalogFacets } from "./catalogFacets";
import {
  catalogActiveFilterCount,
  clearCatalogFilters,
  setCatalogPage,
  setCatalogPageSize,
  withCatalogCategory,
} from "./catalogQuery";
import {
  CatalogEmpty,
  CatalogGrid,
  CatalogShowingLine,
  CatalogSkeletonGrid,
} from "./catalogBrowseParts";
import useCatalogBrowse from "./useCatalogBrowse";

const CatalogBrowseV2: React.FC = () => {
  const { translate } = useTranslation(["myMachinery", "common"]);
  const navigate = useNavigate();
  const { canWrite } = usePermission(Modules.Machinery);

  const c = useCatalogBrowse();
  const { query, setQuery } = c;

  const [brandsExpanded, setBrandsExpanded] = useState(false);
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  const [readinessModel, setReadinessModel] = useState<CatalogModelListItem | null>(null);
  const [addModel, setAddModel] = useState<CatalogModelListItem | null>(null);

  const summaryStrings = useMemo(() => readinessSummaryStrings(translate), [translate]);

  const facets = useMemo(
    () => buildCatalogFacets(c.manufacturers, c.brandSearch),
    [c.manufacturers, c.brandSearch]
  );

  const activeFilters = catalogActiveFilterCount(query);

  const toggleParent = useCallback((id: string) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const onSelectCategory = (code?: string) => {
    // §11: a tab change resets the manufacturer facet AND the brand search box,
    // and returns to page 1. The facet reset lives in `withCatalogCategory`;
    // the brand box is local state, so it is cleared alongside.
    c.setBrandSearch("");
    setBrandsExpanded(false);
    setExpandedParents(new Set());
    setQuery(withCatalogCategory(query, code));
  };

  const onClearAll = () => {
    // `clearSearch`, not `setSearchValue("")`: emptying the box while a
    // keystroke is still in its 300ms debounce would let that keystroke
    // re-apply the search a moment after Clear all wiped it. The hook owns
    // cancel + box + committed value as one operation (useCatalogBrowse.ts).
    c.clearSearch();
    c.setBrandSearch("");
    setBrandsExpanded(false);
    setExpandedParents(new Set());
    setQuery(clearCatalogFilters(query));
  };

  const readinessPresentation = readinessModel
    ? deriveReadinessPresentation(readinessModel, undefined, summaryStrings)
    : null;

  return (
    <div style={{ display: "flex", flex: 1, minHeight: 0, minWidth: 0 }}>
      <CatalogFilterRail
        query={query}
        onQueryChange={setQuery}
        brandSearch={c.brandSearch}
        onBrandSearchChange={c.setBrandSearch}
        facets={facets}
        brandsExpanded={brandsExpanded}
        onToggleBrandsExpanded={() => setBrandsExpanded((prev) => !prev)}
        expandedParents={expandedParents}
        onToggleParent={toggleParent}
        totalCount={c.browseTotalCount}
        taperTypes={c.taperTypes}
        operationTypes={c.operationTypes}
        controlSystemBrands={c.controlSystemBrands}
      />

      <main
        style={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <CatalogBreadcrumb />
        <CatalogTitle>
          <CatalogSearchBox
            value={c.searchValue}
            onChange={c.onSearchChange}
            onSubmit={c.onSearchSubmit}
            onClear={c.clearSearch}
          />
        </CatalogTitle>

        <div style={{ padding: "12px 24px 0", minWidth: 0 }}>
          <CatalogCategoryTabsV2
            categories={c.categories}
            selectedCode={query.categoryCode}
            onSelect={onSelectCategory}
          />
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "12px 24px 24px" }}>
          {activeFilters > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginBottom: 10,
                flexWrap: "wrap",
                minWidth: 0,
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--faint)",
                  letterSpacing: "0.04em",
                }}
              >
                {translate("myMachinery:v2.catalog.activeFilters", { count: activeFilters })}
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onClearAll}
                style={{ height: 26, fontSize: 11.5 }}
              >
                {translate("myMachinery:clearAll")}
              </button>
            </div>
          )}

          {/* §7 fix 5 for the browse surface — see useCatalogBrowse.ts. */}
          {c.failed && (
            <div style={{ marginBottom: 12 }}>
              <RetryNotice
                title={translate("myMachinery:v2.catalog.loadFailed")}
                hint={translate("myMachinery:v2.tabs.loadFailedHint")}
                onRetry={c.retry}
                busy={c.browseLoading}
              />
            </div>
          )}

          {c.browseLoading && c.browseResults.length === 0 ? (
            <CatalogSkeletonGrid />
          ) : c.browseResults.length === 0 ? (
            !c.failed && (
              <CatalogEmpty hasFilters={activeFilters > 0} onClear={onClearAll} />
            )
          ) : (
            <React.Fragment>
              <CatalogShowingLine
                page={query.page}
                pageSize={query.pageSize}
                totalCount={c.browseTotalCount}
              />

              <CatalogGrid>
                {c.browseResults.map((model) => (
                  <CatalogModelCardV2
                    key={model.id}
                    model={model}
                    presentation={deriveReadinessPresentation(model, undefined, summaryStrings)}
                    onOpen={(m) => navigate(`${CATALOG_V2_PATH}/${m.id}`)}
                    onOpenReadiness={setReadinessModel}
                    canWrite={canWrite}
                    onAdd={setAddModel}
                  />
                ))}
              </CatalogGrid>

              <CatalogPagination
                page={query.page}
                pageSize={query.pageSize}
                totalCount={c.browseTotalCount}
                onPage={(page) => setQuery(setCatalogPage(query, page))}
                onPageSize={(size) => setQuery(setCatalogPageSize(query, size))}
              />
            </React.Fragment>
          )}
        </div>
      </main>

      {readinessModel && readinessPresentation && (
        <ReadinessPopupV2
          open
          onOpenChange={(o) => {
            if (!o) setReadinessModel(null);
          }}
          presentation={readinessPresentation}
          machineOrModelName={readinessModel.modelName}
          oemPartnerName={readinessModel.oemPartnerName}
          manufacturerName={readinessModel.manufacturer?.name}
        />
      )}

      {addModel && (
        <CatalogAddDialogV2
          open
          model={addModel}
          onClose={() => setAddModel(null)}
          onCreated={(id) => navigate(catalogAddedPath(id))}
        />
      )}

      <MachineryToastHost />
    </div>
  );
};

export default CatalogBrowseV2;
